/**
 * Badge Picker Component
 *
 * Allows players to select a badge (color + icon identity) for their character.
 * Organized by elemental categories for thematic selection.
 */

import { useState } from 'react';
import { getBadgesByCategory, getBadgeIconsByCategory, CATEGORY_NAMES, type Badge } from './badges';
import { Icon, type IconName } from './Icon';

interface BadgePickerProps {
  currentBadgeId?: string;
  currentBadgeIcon?: string;
  onSelectColor: (badgeId: string) => void;
  onSelectIcon: (badgeIcon: string) => void;
  onClose: () => void;
}

const CATEGORY_ICONS: Record<Badge['category'], IconName> = {
  earth: 'token',
  water: 'crystal',
  fire: 'sparkle',
  air: 'moon',
  arcane: 'wand',
  nature: 'heart',
  shadow: 'skull',
  metal: 'shield',
};

export function BadgePicker({ currentBadgeId, currentBadgeIcon, onSelectColor, onSelectIcon, onClose }: BadgePickerProps) {
  const [tab, setTab] = useState<'color' | 'icon'>('color');
  const badgesByCategory = getBadgesByCategory();
  const iconsByCategory = getBadgeIconsByCategory();

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'var(--color-background)',
          borderRadius: 'var(--radius-lg)',
          border: 'var(--border-width-thin) solid var(--color-border)',
          padding: 'var(--spacing-lg)',
          width: 420,
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--spacing-md)',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 'var(--font-size-lg)',
              fontFamily: 'Georgia, serif',
              fontWeight: 400,
            }}
          >
            Customize Badge
          </h3>
          <button
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--color-text-secondary)',
              cursor: 'pointer',
              padding: 'var(--spacing-xs)',
            }}
          >
            <Icon name="minus" size={16} />
          </button>
        </div>

        {/* Tabs */}
        <div
          style={{
            display: 'flex',
            gap: 'var(--spacing-xs)',
            marginBottom: 'var(--spacing-lg)',
            borderBottom: 'var(--border-width-thin) solid var(--color-border)',
            paddingBottom: 'var(--spacing-sm)',
          }}
        >
          <button
            onClick={() => setTab('color')}
            style={{
              flex: 1,
              padding: 'var(--spacing-sm)',
              background: tab === 'color' ? 'var(--color-primary-subtle)' : 'transparent',
              border: tab === 'color' ? 'var(--border-width-thin) solid var(--color-primary)' : 'var(--border-width-thin) solid transparent',
              borderRadius: 'var(--radius-sm)',
              color: tab === 'color' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontWeight: tab === 'color' ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-xs)',
            }}
          >
            <Icon name="token" size={14} />
            Color
          </button>
          <button
            onClick={() => setTab('icon')}
            style={{
              flex: 1,
              padding: 'var(--spacing-sm)',
              background: tab === 'icon' ? 'var(--color-primary-subtle)' : 'transparent',
              border: tab === 'icon' ? 'var(--border-width-thin) solid var(--color-primary)' : 'var(--border-width-thin) solid transparent',
              borderRadius: 'var(--radius-sm)',
              color: tab === 'icon' ? 'var(--color-primary)' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontWeight: tab === 'icon' ? 600 : 400,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 'var(--spacing-xs)',
            }}
          >
            <Icon name="star" size={14} />
            Icon
          </button>
        </div>

        {/* Color Tab */}
        {tab === 'color' && (
          <>
            {(Object.keys(badgesByCategory) as Badge['category'][]).map((category) => (
              <div key={category} style={{ marginBottom: 'var(--spacing-lg)' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-xs)',
                    marginBottom: 'var(--spacing-sm)',
                  }}
                >
                  <Icon
                    name={CATEGORY_ICONS[category]}
                    size={14}
                    color="var(--color-text-secondary)"
                  />
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {CATEGORY_NAMES[category]}
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--spacing-sm)',
                    flexWrap: 'wrap',
                  }}
                >
                  {badgesByCategory[category].map((badge) => {
                    const isSelected = badge.id === currentBadgeId;
                    return (
                      <button
                        key={badge.id}
                        onClick={() => onSelectColor(badge.id)}
                        title={badge.name}
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          backgroundColor: badge.color,
                          border: isSelected
                            ? `3px solid ${badge.text}`
                            : `2px solid ${badge.accent}`,
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: isSelected
                            ? `0 0 12px ${badge.accent}, 0 0 24px ${badge.color}`
                            : `0 0 4px ${badge.color}40`,
                        }}
                      >
                        {isSelected && (
                          <Icon name="check" size={16} color={badge.text} />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}

        {/* Icon Tab */}
        {tab === 'icon' && (
          <>
            {Object.keys(iconsByCategory).map((category) => (
              <div key={category} style={{ marginBottom: 'var(--spacing-lg)' }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--spacing-xs)',
                    marginBottom: 'var(--spacing-sm)',
                  }}
                >
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 600,
                      letterSpacing: '1px',
                      textTransform: 'uppercase',
                      color: 'var(--color-text-secondary)',
                    }}
                  >
                    {category}
                  </span>
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 'var(--spacing-sm)',
                    flexWrap: 'wrap',
                  }}
                >
                  {iconsByCategory[category].map((iconDef) => {
                    const isSelected = iconDef.id === currentBadgeIcon;
                    return (
                      <button
                        key={iconDef.id}
                        onClick={() => onSelectIcon(iconDef.id)}
                        title={iconDef.label}
                        style={{
                          width: 44,
                          height: 44,
                          borderRadius: 'var(--radius-md)',
                          backgroundColor: isSelected ? 'var(--color-primary-subtle)' : 'var(--color-surface)',
                          border: isSelected
                            ? 'var(--border-width-thin) solid var(--color-primary)'
                            : 'var(--border-width-thin) solid var(--color-border)',
                          cursor: 'pointer',
                          transition: 'all var(--transition-fast)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: isSelected ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                        }}
                      >
                        <Icon name={iconDef.id} size={20} />
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
}
